import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { useCMS } from '../../context/CMSContext';
import { Save, Mail, Phone, MapPin, Clock, RefreshCw } from 'lucide-react';

const AdminContactSettingsPage = () => {
  const { getContactSettings, updateContactSettings } = useCMS();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    hero_title: '',
    hero_subtitle: '',
    email: '',
    phone: '',
    address: '',
    office_hours: '',
    response_time: '',
    cta_title: '',
    cta_subtitle: '',
    cta_button_text: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await getContactSettings();
      if (data) {
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to load contact settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateContactSettings(settings);
      toast.success('Contact settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin h-8 w-8 text-tech-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-navy">Contact Page Settings</h1>
          <p className="text-gray-500">Configure the contact page content and information</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-tech-blue hover:bg-tech-blue/90">
          {saving ? <RefreshCw className="animate-spin mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
          Save Changes
        </Button>
      </div>

      {/* Hero Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-tech-blue" />
            Hero Section
          </CardTitle>
          <CardDescription>Configure the main heading and description</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Hero Title</label>
            <Input
              value={settings.hero_title}
              onChange={(e) => handleChange('hero_title', e.target.value)}
              placeholder="Get in Touch"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Hero Subtitle</label>
            <textarea
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-tech-blue/20"
              rows={3}
              value={settings.hero_subtitle}
              onChange={(e) => handleChange('hero_subtitle', e.target.value)}
              placeholder="We'd love to hear from you..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-care-teal" />
            Contact Information
          </CardTitle>
          <CardDescription>Set your organization's contact details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                <Mail className="inline h-4 w-4 mr-1" />
                Email Address
              </label>
              <Input
                type="email"
                value={settings.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="info@getcare360.com.au"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                <Phone className="inline h-4 w-4 mr-1" />
                Phone Number
              </label>
              <Input
                value={settings.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="1800 951 GET"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              <MapPin className="inline h-4 w-4 mr-1" />
              Address
            </label>
            <Input
              value={settings.address}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="Australia Wide"
            />
          </div>
        </CardContent>
      </Card>

      {/* Office Hours */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-500" />
            Office Hours & Response Time
          </CardTitle>
          <CardDescription>Set your availability information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Office Hours</label>
            <Input
              value={settings.office_hours}
              onChange={(e) => handleChange('office_hours', e.target.value)}
              placeholder="Monday - Friday: 9:00 AM - 5:00 PM AEST"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Response Time Message</label>
            <Input
              value={settings.response_time}
              onChange={(e) => handleChange('response_time', e.target.value)}
              placeholder="We typically respond within 24 hours"
            />
          </div>
        </CardContent>
      </Card>

      {/* CTA Section */}
      <Card>
        <CardHeader>
          <CardTitle>Call to Action Section</CardTitle>
          <CardDescription>Configure the booking CTA section</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">CTA Title</label>
            <Input
              value={settings.cta_title}
              onChange={(e) => handleChange('cta_title', e.target.value)}
              placeholder="Prefer to Book a Call?"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">CTA Subtitle</label>
            <Input
              value={settings.cta_subtitle}
              onChange={(e) => handleChange('cta_subtitle', e.target.value)}
              placeholder="Schedule a free strategy session..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">CTA Button Text</label>
            <Input
              value={settings.cta_button_text}
              onChange={(e) => handleChange('cta_button_text', e.target.value)}
              placeholder="Book Free Strategy Call"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminContactSettingsPage;
